import Dexie, { type EntityTable } from "dexie";
import type { PersistedClient } from "@tanstack/react-query-persist-client";

export type CommandStatus =
  | "queued"
  | "sending"
  | "acknowledged"
  | "confirmed"
  | "failed"
  | "conflict"
  | "cancelled";

export interface QueuedCommand {
  clientMutationId: string;
  clientId: string;
  commandType: "order.changeStatus";
  entityType: "order";
  entityId: number;
  expectedVersion: number;
  payload: { newStatus: string };
  rollback: { status: string; entityVersion: number };
  createdAt: string;
  nextAttemptAt: number;
  attemptCount: number;
  status: CommandStatus;
  error?: string;
}

interface SettingRecord {
  key: string;
  value: unknown;
}

interface QueryCacheRecord {
  key: "tanstack-query";
  value: PersistedClient;
}

class EurocutDatabase extends Dexie {
  commands!: EntityTable<QueuedCommand, "clientMutationId">;
  settings!: EntityTable<SettingRecord, "key">;
  queryCache!: EntityTable<QueryCacheRecord, "key">;

  constructor() {
    super("eurocut-erp");
    this.version(1).stores({
      commands:
        "&clientMutationId, [entityType+entityId], status, createdAt, nextAttemptAt",
      settings: "&key",
      queryCache: "&key",
    });
  }
}

export const db = new EurocutDatabase();

export async function getOrCreateClientId(): Promise<string> {
  const existing = await db.settings.get("client-id");
  if (typeof existing?.value === "string") return existing.value;
  const clientId = crypto.randomUUID();
  await db.settings.put({ key: "client-id", value: clientId });
  return clientId;
}

export async function setSetting(key: string, value: unknown): Promise<void> {
  await db.settings.put({ key, value });
}

export async function getSetting<T>(key: string): Promise<T | undefined> {
  return (await db.settings.get(key))?.value as T | undefined;
}
